/**
 * PATH: packages/bills/src/components/useDragPan.ts
 * FILE_ID: BILLS-PKG-CMP-DRAGPAN-001
 * PURPOSE: Drag-to-pan a scrollable element with the mouse — so a
 *          zoomed-in PDF / image can be moved around without hunting
 *          for the scrollbars.
 *
 * WHY: Once zoom > fit the document overflows the pane; trackpad users
 *      can two-finger scroll, but mouse users had no way to move the
 *      page. Grab-and-drag is the universal viewer gesture (Acrobat,
 *      Figma, Maps). Pure scrollLeft/scrollTop math, no extra deps.
 */

import { useEffect, type RefObject } from 'react';

/**
 * Enable click-drag panning on `ref`'s scroll position.
 * @param ref      scroll container (must be `overflow:auto`)
 * @param enabled  only attach when the content can actually overflow
 */
export function useDragPan(
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
): void {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    let dragging = false;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;

    function onDown(e: MouseEvent) {
      // Left button only; ignore clicks on links/buttons so "Open in new
      // tab" etc. still work.
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest('a,button,input,textarea,select')) return;
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      startLeft = el!.scrollLeft;
      startTop = el!.scrollTop;
      el!.style.cursor = 'grabbing';
      el!.style.userSelect = 'none';
    }
    function onMove(e: MouseEvent) {
      if (!dragging) return;
      el!.scrollLeft = startLeft - (e.clientX - startX);
      el!.scrollTop = startTop - (e.clientY - startY);
    }
    function onUp() {
      dragging = false;
      el!.style.cursor = 'grab';
      el!.style.removeProperty('user-select');
    }

    el.style.cursor = 'grab';
    el.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      el.style.removeProperty('cursor');
      el.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [ref, enabled]);
}
